import { parsePassCode } from '@sentra/contracts';
import { type ReactNode, useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/design/components/input';
import { Label } from '@/design/components/label';
import type { LoadedDetector } from '@/features/scan/detector';
import { loadCodeDetector } from '@/features/scan/detector';

/**
 * Reading a pass out of a photograph somebody took of it.
 *
 * **The third way in, after the hardware wedge and the camera**, and the one that covers the case
 * neither of the others can: a person arrives with a photograph of their band on their phone,
 * because the band itself is at home. It is also what a terminal with no camera and a broken
 * scanner falls back to, short of typing twenty-one characters.
 *
 * **The file is handed to the decoder as a `Blob`, and nothing is drawn.** Both the native
 * `BarcodeDetector` and the ponyfill accept an `ImageBitmapSource`, of which a `Blob` is one, so
 * there is no canvas here and no `createImageBitmap`: the bytes never become pixels this
 * application holds, which is one fewer copy of somebody's photograph in this process's heap.
 *
 * **Nothing is uploaded.** The decode is local and the only thing that leaves this browser is the
 * code, through the same resolve request a scan makes. A photograph of a pass is not a photograph
 * this product has any business storing.
 */

/** What the panel is doing. Each failure is a sentence, because each has a different next step. */
type UploadState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'decoding' }
  /** The picture was read and held no code: the wrong photograph, or one too blurred to decode. */
  | { readonly kind: 'no_code' }
  /** The decoder could not run at all, which is a property of the browser rather than the file. */
  | { readonly kind: 'unsupported' }
  | { readonly kind: 'failed' };

export function ScanUpload({
  onCode,
  loadDetector = loadCodeDetector,
}: Readonly<{
  onCode: (code: string) => void;
  /** Injected, because `jsdom` runs no WebAssembly and decodes no image. */
  loadDetector?: () => Promise<LoadedDetector>;
}>): ReactNode {
  const { t } = useTranslation();
  const fieldId = useId();
  const [state, setState] = useState<UploadState>({ kind: 'idle' });

  const read = useCallback(
    async (file: File): Promise<void> => {
      setState({ kind: 'decoding' });
      let loaded: LoadedDetector;
      try {
        loaded = await loadDetector();
      } catch {
        setState({ kind: 'unsupported' });
        return;
      }
      try {
        const codes = await loaded.detector.detect(file);
        for (const candidate of codes) {
          const parsed = parsePassCode(candidate.rawValue);
          if (parsed.ok) {
            setState({ kind: 'idle' });
            onCode(parsed.code);
            return;
          }
        }
        // A picture that decoded to something which is not one of our codes is the same finding as
        // one that decoded to nothing: there is no pass in it. Saying "we found a barcode, but"
        // would send the operator looking for a fault in a photograph of a supermarket receipt.
        setState({ kind: 'no_code' });
      } catch {
        setState({ kind: 'failed' });
      }
    },
    [loadDetector, onCode],
  );

  return (
    <div className="grid gap-2">
      <Label htmlFor={fieldId}>{t('scan.upload.label')}</Label>
      <Input
        id={fieldId}
        type="file"
        accept="image/*"
        onChange={(event) => {
          const [file] = event.target.files ?? [];
          if (file !== undefined) {
            void read(file);
          }
        }}
      />
      <p className="text-sm text-muted-foreground">{t('scan.upload.hint')}</p>
      {state.kind !== 'idle' && (
        <p role="status" data-upload-state={state.kind} className="text-sm">
          {t(`scan.upload.${UPLOAD_MESSAGES[state.kind]}`)}
        </p>
      )}
    </div>
  );
}

/** One catalogue entry per state, total over the union so a new state cannot render as nothing. */
const UPLOAD_MESSAGES = {
  decoding: 'decoding',
  no_code: 'noCode',
  unsupported: 'unsupported',
  failed: 'failed',
} as const satisfies Record<Exclude<UploadState['kind'], 'idle'>, string>;
